<script setup lang="ts">

import * as FlipCoinApi from '@/api/finance/game/flipCoin';
import { fenToYuan } from '@/utils/price';
import { formatToDateTime } from '@/utils/dateUtil';

defineOptions({
  name: 'FlipCoinHistoryTable'
});

interface Column {
  name: string;
  label: string;
  field: string | ((row: any) => any);
  align?: 'left' | 'right' | 'center';
  format?: (val: any, row: any) => any;
  sortable?: boolean;
  required?: boolean;
}

const FlipCoinType = FlipCoinApi.FlipCoinType;

const resultFormat = (result: string) => {
  return FlipCoinType.FRONT.value === Number(result) ? FlipCoinType.FRONT.name : FlipCoinType.BACK.name;
};

const calcPayout = (row: any) => {
  if (row.reward > 0) {
    return row.reward;
  }
  return 0 - row.price;
};

const columns: Column[] = [
  {
    name: 'userResult',
    label: 'My Bet',
    align: 'center',
    field: 'userResult',
    format: val => resultFormat(val)
  },
  {
    name: 'gameResult',
    label: 'Result',
    align: 'center',
    field: 'gameResult',
    format: val => resultFormat(val)
  },
  {
    name: 'price',
    label: 'Bet Amount',
    align: 'center',
    field: 'price',
    format: val => fenToYuan(val)
  },
  {
    name: 'reward',
    label: 'Payout',
    align: 'center',
    field: 'reward',
    format: (val, row) => fenToYuan(calcPayout(row))
  },
  {
    name: 'createTime',
    label: 'Date',
    align: 'left',
    field: 'createTime',
    format: val => formatToDateTime(val)
  }
];

const rows = ref([] as any);
const loading = ref(false);

// 分页查询
const queryParams = ref({
  pageNo: 1, // 页数
  pageSize: 20, // 每页显示数量
  game: 1 // 游戏类型 固定1   // 翻硬币1  九宫格抽奖2
});

const total = ref(2); // 总条数

/** 查询列表 */
const getList = async () => {
  loading.value = true;
  try {
    const data = await FlipCoinApi.getHistoryPage(unref(queryParams));
    rows.value.push(...data.list);
    total.value = data.total;

    queryParams.value.pageNo++;
  } finally {
    loading.value = false;
  }
};

const pagination = { rowsPerPage: 0 };

const onScroll = ({ to, ref }) => {
  const lastIndex = rows.value.length - 1;

  if (loading.value !== true // 列表不在加载数据
    && rows.value.length < total.value // 列表还有数据没加载完
    && to >= lastIndex - 10 // 提前10个开始加载数据
  ) {
    loading.value = true;

    getList().finally(() => {
      nextTick(() => {
        ref.refresh();
        loading.value = false;
      });
    });
  }
};

onMounted(() => {
  getList();
});

</script>

<template>
  <q-card class="bg-common radius-top-md">
    <q-table
      class="bg-common text-primary my-sticky-dynamic"
      flat
      title="My Stats"
      :rows="rows"
      :columns="columns"
      :loading="loading"
      row-key="name"
      virtual-scroll
      :virtual-scroll-item-size="48"
      :virtual-scroll-sticky-size-start="48"
      :pagination="pagination"
      :rows-per-page-options="[0]"
      no-data-label="No Game Record"
      @virtual-scroll="onScroll"
    >
      <template v-slot:no-data="{ icon, message }">
        <div class="full-width row flex-center text-accent q-gutter-sm">
          <q-icon size="2em" name="sentiment_dissatisfied" />
          <span>
            {{ message }}
          </span>
          <q-icon size="2em" :name="icon" />
        </div>
      </template>
    </q-table>
  </q-card>
</template>

<style lang="scss" scoped>
.my-sticky-dynamic {
  /* height or max-height is important */
  height: 420px;

  :deep(.q-table__top),
  :deep(.q-table__bottom),
  :deep(thead tr:first-child th) {
    /* bg color is important for th; just specify one */
    //background-color: #fff;
    color: $primary;
    background-color: $common;
  }

  :deep(.q-table__middle) {

    thead tr th {
      position: sticky;
      z-index: 1;
    }

    /* this will be the loading indicator */
    thead tr:last-child th {
      /* height of all previous header rows */
      top: 48px;
    }

    thead tr:first-child th {
      top: 0;
    }
  }

  :deep(.q-table__bottom) {
    //display: none;
  }
}
</style>